import { StateCreator } from "zustand";
import {
  MetadataSlice,
  ValidationEntry,
  isAssetValidationEntry,
} from "./types";
import { UpdateAssetDto, UploadWithAssetsDto } from "generated";
import { Store } from "../../types";
import { ErrorResponse } from "@/hooks/types";

export const createMetadataSlice: StateCreator<Store, [], [], MetadataSlice> = (
  set,
) => ({
  assets: [],
  status: "LOADING",
  uploadState: "DRAFT",
  error: undefined,
  comment: "",
  validationErrors: [],

  removeAsset: (assetId: number) => {
    set((state) => ({
      ...state,
      assets: state.assets.filter((asset) => asset.id !== assetId),
    }));
  },

  initializeMetadata: (upload: UploadWithAssetsDto, assetIds?: string) => {
    const filteredAssetIds =
      assetIds?.split(",").map((s) => Number(s)) ||
      upload.assets.map((asset) => asset.id);

    const assets = upload.assets.filter((asset) =>
      filteredAssetIds.includes(asset.id),
    );

    const firstAssetWithoutIsSocialMediaOnly = assets.find(
      (a) => !a.relatedAssetMetadata?.isOnlySocialMedia,
    );
    const selectedId = firstAssetWithoutIsSocialMediaOnly
      ? firstAssetWithoutIsSocialMediaOnly.id
      : assets[0]?.id;

    set((state) => ({
      ...state,
      uploadId: upload.id,
      comment: "",
      status: "SUCCESS",
      uploadState: upload.state,
      assets: assets.map((asset) => ({
        ...asset,
        name: asset.name || asset.filename,
        isActive: asset.id === selectedId,
        isChecked: true,
      })),
    }));
  },

  setActiveAsset: (assetId: number) => {
    set((state) => ({
      ...state,
      assets: state.assets.map((asset) => ({
        ...asset,
        isActive: asset.id === assetId,
        isChecked: asset.id === assetId,
      })),
    }));
  },

  toggleAssetCheckbox: (assetId: number) => {
    set((state) => ({
      ...state,
      assets: state.assets.map((asset) =>
        asset.id === assetId
          ? { ...asset, isChecked: !asset.isChecked }
          : asset,
      ),
    }));
  },

  toggleSetAllCheckbox: (checked: boolean) => {
    set((state) => ({
      ...state,
      assets: state.assets.map((asset) => ({
        ...asset,
        isChecked: asset.isActive || checked,
      })),
    }));
  },

  updateAssetMetadataSuccessful: (updatedAssets: UpdateAssetDto[]) => {
    const assetLookup = updatedAssets.reduce(
      (acc, asset) => {
        acc[asset.id!] = asset;
        return acc;
      },
      {} as Record<number, UpdateAssetDto>,
    );

    set((state) => ({
      ...state,
      status: "SUCCESS",
      validationErrors: state.validationErrors.filter((e) => {
        if (!isAssetValidationEntry(e)) return true;

        if (e.field === "placeholder") return false;
        const updatedAsset = assetLookup[e.assetId];
        if (!updatedAsset) return true;
        const fieldValue = updatedAsset[e.field as keyof UpdateAssetDto];
        if (e.field === "endOfNegotiatedUsageRights" && !fieldValue) {
          return !updatedAsset.confirmEndOfNegotiatedUsageRights;
        }
        if (e.field === "productionOrderedBy" && !fieldValue) {
          return updatedAsset.producedBy === "AGENCY";
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.length === 0;
        }
        return !fieldValue;
      }),
      assets: state.assets.map((asset) => {
        const updatedAsset = assetLookup[asset.id!];
        return updatedAsset ? { ...asset, ...updatedAsset } : asset;
      }),
    }));
  },

  setMetadataError: (error: ErrorResponse) => {
    set((state) => ({
      ...state,
      status: "ERROR",
      error,
    }));
  },

  setLoading: () => {
    set((state) => ({
      ...state,
      status: "LOADING",
    }));
  },

  setComment: (comment: string) => {
    set((state) => ({
      ...state,
      comment,
    }));
  },

  validationRequested: () => {
    set((state) => ({
      ...state,
      validationErrors: [
        {
          assetId: 0,
          field: "placeholder",
          message: "placeholder",
        },
      ],
    }));
  },

  setValidationError: (errors: ValidationEntry[]) => {
    set((state) => ({
      ...state,
      validationErrors: errors,
    }));
  },

  clearPlaceholderErrors: () => {
    set((state) => ({
      ...state,
      validationErrors: [],
    }));
  },
});
